import React from 'react';
import { PhotoCarousel } from '@shared/components/PhotoCarousel';
import type { PhotoCarouselProps } from '../PhotoCarousel/PhotoCarousel';
import type { Maybe } from '@graphql/generated-base';

export const photosToCarouselImages = (photos?: ReadonlyArray<string>, altText?: Maybe<string>): ReadonlyArray<Readonly<{ src: string; alt: string }>> => {
  return (photos?.map(photo => ({ src: photo, alt: altText || '' })) as ReadonlyArray<{ src: string; alt: string }>) ?? [];
};

/**
 * Since hotel objects comes from different providers/API's with different fields and there is no common required fields.
 * Defined a common interface for the hotel input to the HotelPhotoCarousel component.
 **/
interface HotelInput
  extends Readonly<{
    id?: Maybe<string>;
    type?: Maybe<string>;
    name?: Maybe<string>;
    displayName?: Maybe<string>;
    joyPlaceId?: Maybe<string>;
    zhId?: Maybe<string>;
    accommodationPlaceId?: Maybe<string>;
  }> {}

interface HotelPhotoCarouselProps
  extends Omit<PhotoCarouselProps, 'extraInfo'>,
    Readonly<{
      hotel: HotelInput;
    }> {}

/**
 * A carousel component specifically for displaying hotel photos.
 * It ensures that the hotel has a valid identifier before rendering.
 *
 * @param hotel - The hotel information including identifiers and display name.
 * @param restProps - Additional props to be passed to the PhotoCarousel component.
 * @returns A PhotoCarousel component configured for the specified hotel.
 */
export const HotelPhotoCarousel: React.FC<HotelPhotoCarouselProps> = ({ hotel, children, ...restProps }) => {
  return (
    <PhotoCarousel
      extraInfo={{
        id: hotel.id,
        type: hotel.type,
        displayName: hotel.displayName || hotel.name,
        joyPlaceId: hotel.joyPlaceId,
        zhId: hotel.zhId,
        accommodationPlaceId: hotel.accommodationPlaceId,
      }}
      {...restProps}
    >
      {children}
    </PhotoCarousel>
  );
};
